// src/utils/assets/assetReplayUtils.js

/**
 * =====================================================
 * ASSET REPLAY UTILITIES
 * =====================================================
 *
 * PURPOSE:
 * - Pure utility functions for asset ledger replay
 * - No Firestore calls — pure data transformation
 * - Used by assetLedgerService.js
 *
 * RULES:
 * - All replay logic uses workflow_state — not .status
 * - Only FINALIZED transactions change custody
 * - No raw strings — all enums
 * =====================================================
 */

import { WORKFLOW_STATES } from "../../services/assets/workflowStates";

// =====================================================
// SORT TRANSACTIONS CHRONOLOGICALLY
// Primary: created_at asc
// Tiebreak: document id lexicographic
// =====================================================

export const sortTransactionsChronologically = (transactions) => {

    if (!transactions || transactions.length === 0) return [];

    return [...transactions].sort((a, b) => {

        const aTime = a.created_at?.seconds || 0;
        const bTime = b.created_at?.seconds || 0;

        if (aTime !== bTime) return aTime - bTime;

        return (a.id || "").localeCompare(b.id || "");
    });
};

// =====================================================
// FILTER APPROVED TRANSACTIONS
// Only FINALIZED — these are the only ones that
// changed custody
// =====================================================

export const filterApprovedTransactions = (transactions) => {

    if (!transactions || transactions.length === 0) return [];

    return transactions.filter(
        (t) => t.workflow_state === WORKFLOW_STATES.FINALIZED
    );
};

// =====================================================
// FILTER ACTIVE PENDING TRANSACTIONS
// =====================================================

export const filterPendingTransactions = (transactions) => {

    if (!transactions || transactions.length === 0) return [];

    return transactions.filter(
        (t) =>
            t.workflow_state === WORKFLOW_STATES.PENDING_ACCEPTANCE ||
            t.workflow_state === WORKFLOW_STATES.RETURN_PENDING
    );
};

// =====================================================
// NORMALIZE TIMESTAMP
// Converts Firestore Timestamp to JS Date or null
// =====================================================

export const normalizeTimestamp = (timestamp) => {

    if (!timestamp) return null;

    try {

        if (timestamp?.seconds) {
            return new Date(timestamp.seconds * 1000);
        }

        if (timestamp instanceof Date) {
            return timestamp;
        }

        return null;

    } catch {
        return null;
    }
};

// =====================================================
// FORMAT TIMESTAMP
// Returns localeString or "-"
// =====================================================

export const formatTimestamp = (timestamp) => {

    const date = normalizeTimestamp(timestamp);

    if (!date) return "-";

    try {
        return date.toLocaleString();
    } catch {
        return "-";
    }
};

// =====================================================
// REPLAY HOLDER HISTORY
// Walks through sorted transactions in order
// Only FINALIZED transactions mutate holder
// Returns array of replay steps
// =====================================================

/**
 * @param {Array} sortedTransactions - chronologically sorted
 * @param {Object} initialHolder - { holder_type, holder_id }
 * @returns {Array} replaySteps
 */

export const replayHolderHistory = (
    sortedTransactions,
    initialHolder
) => {

    if (!sortedTransactions || sortedTransactions.length === 0) {
        return [];
    }

    let currentHolder = {
        holder_type: initialHolder?.holder_type || "STORE",
        holder_id:   initialHolder?.holder_id   || null,
        holder_name: initialHolder?.holder_name  || "MAIN STORE",
    };

    return sortedTransactions.map((transaction) => {

        const previousHolder = { ...currentHolder };

        // ─────────────────────────────────────────
        // Only FINALIZED changes custody
        // REJECTED / CANCELLED — holder unchanged
        // ─────────────────────────────────────────

        if (transaction.workflow_state === WORKFLOW_STATES.FINALIZED) {

            currentHolder = {
                holder_type: transaction.to_type   || currentHolder.holder_type,
                holder_id:   transaction.to_id     || currentHolder.holder_id,
                holder_name: transaction.to_holder_name ||
                             transaction.to_snapshot?.holder_name ||
                             transaction.to_id     ||
                             currentHolder.holder_name,
            };
        }

        return {
            transaction_id:   transaction.id,
            workflow_state:   transaction.workflow_state,
            workflow_type:    transaction.workflow_type,
            transaction_class: transaction.transaction_class,
            status:           transaction.workflow_state, // UI compat alias
            previous_holder:  previousHolder,
            resulting_holder: { ...currentHolder },
            created_at:       transaction.created_at,
        };
    });
};

// =====================================================
// DERIVE HOLDER FROM REPLAY
// Returns final current holder after full replay
// =====================================================

/**
 * @param {Array} sortedTransactions - chronologically sorted
 * @param {Object} initialHolder - { holder_type, holder_id }
 * @returns {Object} currentHolder
 */

export const deriveHolderFromReplay = (
    sortedTransactions,
    initialHolder
) => {

    if (!sortedTransactions || sortedTransactions.length === 0) {
        return {
            holder_type: initialHolder?.holder_type || "STORE",
            holder_id:   initialHolder?.holder_id   || null,
            holder_name: initialHolder?.holder_name  || "MAIN STORE",
            source:      "initial_state",
        };
    }

    let currentHolder = {
        holder_type: initialHolder?.holder_type || "STORE",
        holder_id:   initialHolder?.holder_id   || null,
        holder_name: initialHolder?.holder_name  || "MAIN STORE",
    };

    for (const transaction of sortedTransactions) {

        if (transaction.workflow_state === WORKFLOW_STATES.FINALIZED) {

            currentHolder = {
                holder_type: transaction.to_type   || currentHolder.holder_type,
                holder_id:   transaction.to_id     || currentHolder.holder_id,
                holder_name: transaction.to_holder_name ||
                             transaction.to_snapshot?.holder_name ||
                             transaction.to_id     ||
                             currentHolder.holder_name,
            };
        }
    }

    return {
        ...currentHolder,
        source: "replay",
    };
};

// =====================================================
// VERIFY REPLAY CONSISTENCY
// Compares asset_master snapshot vs ledger replay
// =====================================================

/**
 * @param {Object} assetMaster - asset_master document
 * @param {Object} replayHolder - result of deriveHolderFromReplay
 * @returns {{ replay_safe: boolean, snapshot_holder, replay_holder }}
 */

export const verifyReplayConsistency = (assetMaster, replayHolder) => {

    const snapshotHolder = {
        holder_type: assetMaster?.current_holder_type || null,
        holder_id:   assetMaster?.current_holder_id   || null,
    };

    const replaySafe =
        String(snapshotHolder.holder_type || "") ===
        String(replayHolder.holder_type   || "")
        &&
        String(snapshotHolder.holder_id   || "") ===
        String(replayHolder.holder_id     || "");

    return {
        replay_safe:     replaySafe,
        snapshot_holder: snapshotHolder,
        replay_holder:   replayHolder,
    };
};

// =====================================================
// GET HOLDER DISPLAY STRING
// Safe fallback chain for any holder object
// =====================================================

export const getHolderDisplayString = (holder) => {

    if (!holder) return "N/A";

    return (
        holder.holder_name ||
        holder.current_holder_name ||
        holder.holder_id ||
        holder.current_holder_id ||
        `${holder.holder_type || "-"} / ${holder.holder_id || "-"}`
    );
};